import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import reportWebVitals from "./reportWebVitals";
import App from "./App";
import { BrowserRouter } from "react-router-dom";

import {
  initTelemetry,
  TELEMETRY_EVENT_TYPES,
  handlePwaVersion,
  setupPwaTelemetry,
  getApplicationVersion,
  getClientId,
  logService
} from "@seims/shared";
import { getToken, initKeycloak, isOfflineSessionAvailable, hasValidCachedSession } from "@seims/shared/src/security";
import { registerServiceWorker } from "@seims/shared/src/components/eventspwa/util/registerServiceWorker";
import { preloadOfflineLookups } from "./services/offlineLookupSync";
import { getMergedOfflineSyncStats, startOfflineSyncStatsTracking } from "./telemetry/offlineSyncStats";
import { ensureDatabaseIntegrity } from "@seims/shared/src/services/db";

import { validateQueueConfig } from "@seims/shared/src/hooks/offlineQueueConfig";

const EVENTS_APP_NAME = "EventsApp";

const telemetryApiBase =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_BACKEND_API
    : window?._CONFIGS?.REACT_APP_BACKEND_API;

const telemetry = initTelemetry({
  appName: EVENTS_APP_NAME,
  appVersion: getApplicationVersion(),
  clientId: getClientId(),
  endpoint: `${(telemetryApiBase || "").replace(/\/$/, "")}/system/log/application/errors/events`,
  getToken,
  getOfflineSyncStats: getMergedOfflineSyncStats,
});

startOfflineSyncStatsTracking();

telemetry?.initGlobalErrorCapture();
telemetry?.initNetworkAutoFlush();
telemetry?.initWebVitalsTracking();

const sendRuntimeTelemetry = async ({
  applicationId = 1,
} = {}) => {
  const snapshot = telemetry?.getRuntimeSnapshot?.();
  const appName = "SEIMS Events App";
  if (!snapshot) return;

  try {
    const resolvedApplicationId = await logService
      .getTelemetryApplicationId(appName)
      .catch(() => null);

    const response = await logService.sendTelemetry({
      applicationid: resolvedApplicationId ?? applicationId,
      payload: snapshot,
    });

    if (response?.status === 200) {
      console.log("[Telemetry] Runtime snapshot sent successfully");
    } else {
      console.warn("[Telemetry] Failed to send runtime snapshot", response);
    }
  } catch (err) {
    console.warn("[Telemetry] Failed to send runtime snapshot", err);
  }
};

if (typeof window !== "undefined") {
  window.addEventListener(
    "load",
    () => {
      console.log("[Telemetry] EventsApp runtime snapshot", telemetry?.getRuntimeSnapshot?.())
      sendRuntimeTelemetry();
    },
    { once: true },
  );
}

if (typeof window !== "undefined" && !window.__eventsappInstallPromptListenerBound) {
  window.__eventsappInstallPromptListenerBound = true;
  window.__eventsappDeferredInstallPrompt = null;

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    window.__eventsappDeferredInstallPrompt = event;
    window.dispatchEvent(new Event("eventsapp:installable"));
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
let failedStart = false;
let message;
const queryClient = new QueryClient({
  defaultOptions: {},
});

setupPwaTelemetry({ appName: EVENTS_APP_NAME });

registerServiceWorker({
  onWorkerMessage: async (detail) => {
    if (!detail || typeof detail !== "object") {
      return;
    }

    telemetry?.track(TELEMETRY_EVENT_TYPES.SW_SYNC_EVENT, {
      swMessageType: detail.type,
      swVersion: detail.swVersion || null,
      replayed: detail.replayed || null,
      remaining: detail.remaining || null,
      reason: detail.reason || null,
    });

    if (detail.type === "SW_ERROR") {
      telemetry?.trackSwError(
        { message: detail.message || "SW error", name: "ServiceWorkerError" },
        { url: detail.url, method: detail.method },
      );
    }

    if (detail.type === "SW_FETCH_METRIC") {
      telemetry?.trackPerformanceMetric("SW_FETCH", detail.durationMs ?? null, {
        url: detail.url,
        method: detail.method,
        source: detail.source,
        status: detail.status,
        cacheHit: detail.source === "cache",
      });
    }

    if (detail.type === "SYNC_FAILED") {
      telemetry?.trackSyncFailure(detail.reason || "network", {
        url: detail.url,
        method: detail.method,
        message: detail.message,
      });
    }

    const swVersion = detail.swVersion;
    const isVersionEvent = detail.type === "SW_VERSION" || detail.type === "SW_VERSION_ACTIVE";
    if (!isVersionEvent || !swVersion) {
      return;
    }

    const outcome = await handlePwaVersion({ appName: EVENTS_APP_NAME, swVersion });
    if (outcome.rollbackApplied) {
      window.location.reload();
    }
  },
});

const warmOfflineLookups = () => {
  if (typeof window === "undefined") {
    return;
  }

  if (window.__eventsappLookupWarmupStarted) {
    return;
  }

  window.__eventsappLookupWarmupStarted = true;

  if (!navigator.onLine) {
    return;
  }

  preloadOfflineLookups().catch(() => {
    // Keep app startup resilient even if background lookup preload fails.
  });
};

const startApp = () => {
  if (isOfflineSessionAvailable() || hasValidCachedSession()) {
    return renderApp(false);
  }

  initKeycloak(renderApp).then((instance) => {
    failedStart = instance.failedToStart;
    if (failedStart) {
      message =
        "One of the application services failed to start. Please contact administrator";
      return renderApp(true);
    } else renderApp(false);
  });
};

const renderApp = async () => {
  warmOfflineLookups();
  await ensureDatabaseIntegrity();

  validateQueueConfig();

  root.render(
    // <React.StrictMode>
    <BrowserRouter>
      {/* <Offline /> */}
      <QueryClientProvider client={queryClient}>
        <App failedStart={failedStart} message={message} />

        <ReactQueryDevtools />
      </QueryClientProvider>
    </BrowserRouter>

    // </React.StrictMode>
  );

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals();
};

startApp();
